import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { customServiceOrderApi } from '../../api/customServiceOrder';

// 定制服务订单状态常量
export const CUSTOM_SERVICE_ORDER_STATUS = {
  PENDING_PAYMENT: 0,
  PENDING_SHIPMENT: 1,
  PENDING_RECEIPT: 2,
  COMPLETED: 3,
  CLOSED: 4,
  EXCEPTION: 5
};

// 获取定制服务订单列表
export const fetchCustomServiceOrderList = createAsyncThunk(
  'customServiceOrder/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.getCustomServiceOrderList(params);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.current || 1,
        pageSize: data.size || params.size || 10
      };
    } catch (error) {
      console.error('获取定制服务订单列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取定制服务订单列表失败' });
    }
  }
);

// 获取定制服务订单详情
export const fetchCustomServiceOrderDetail = createAsyncThunk(
  'customServiceOrder/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.getCustomServiceOrderDetail(id);
      return response.data || response;
    } catch (error) {
      console.error('获取定制服务订单详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取定制服务订单详情失败' });
    }
  }
);

// 更新订单状态
export const updateCustomServiceOrderStatus = createAsyncThunk(
  'customServiceOrder/updateStatus',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.updateCustomServiceOrderStatus(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 分配管理员
export const assignCustomServiceOrder = createAsyncThunk(
  'customServiceOrder/assign',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.assignCustomServiceOrder(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 设置预计交付时间
export const setDeliveryTime = createAsyncThunk(
  'customServiceOrder/setDeliveryTime',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.setDeliveryTime(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 完成订单
export const completeCustomServiceOrder = createAsyncThunk(
  'customServiceOrder/complete',
  async (id, { rejectWithValue }) => {
    try {
      await customServiceOrderApi.completeCustomServiceOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 关闭订单
export const cancelCustomServiceOrder = createAsyncThunk(
  'customServiceOrder/cancel',
  async (id, { rejectWithValue }) => {
    try {
      await customServiceOrderApi.cancelCustomServiceOrder(id);
      message.success('关闭订单成功');
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '关闭订单失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 发货
export const shipCustomServiceOrder = createAsyncThunk(
  'customServiceOrder/ship',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await customServiceOrderApi.shipOrder(id, data);
      message.success('发货成功');
      return { id, ...response.data };
    } catch (error) {
      const errorMsg = error.response?.data?.message || '发货失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除订单（软删除）
export const deleteCustomServiceOrder = createAsyncThunk(
  'customServiceOrder/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await customServiceOrderApi.deleteCustomServiceOrder(id);
      message.success('删除成功');
      // 刷新列表
      dispatch(fetchCustomServiceOrderList());
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 定制服务订单 Slice
const customServiceOrderSlice = createSlice({
  name: 'customServiceOrder',
  initialState: {
    list: [],
    currentOrder: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchCustomServiceOrderList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomServiceOrderList.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload || {};
        state.list = data.list || [];
        state.pagination.total = data.total || 0;
        state.pagination.current = data.current || state.pagination.current;
        state.pagination.pageSize = data.pageSize || state.pagination.pageSize;
      })
      .addCase(fetchCustomServiceOrderList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchCustomServiceOrderDetail.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
      })
      
      // 更新状态
      .addCase(updateCustomServiceOrderStatus.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 分配管理员
      .addCase(assignCustomServiceOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 设置交付时间
      .addCase(setDeliveryTime.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 完成订单
      .addCase(completeCustomServiceOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload);
        if (index !== -1) {
          state.list[index].status = 'COMPLETED';
        }
        if (state.currentOrder && state.currentOrder.id === action.payload) {
          state.currentOrder.status = 'COMPLETED';
        }
      })
      
      // 关闭订单
      .addCase(cancelCustomServiceOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload);
        if (index !== -1) {
          state.list[index].status = 'CLOSED';
        }
        if (state.currentOrder && state.currentOrder.id === action.payload) {
          state.currentOrder.status = 'CLOSED';
        }
      })
      
      // 发货
      .addCase(shipCustomServiceOrder.fulfilled, (state, action) => {
        const updatedOrder = action.payload;
        const index = state.list.findIndex(item => item.id === updatedOrder.id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedOrder };
        }
        if (state.currentOrder && state.currentOrder.id === updatedOrder.id) {
          state.currentOrder = { ...state.currentOrder, ...updatedOrder };
        }
      })
      
      // 删除订单
      .addCase(deleteCustomServiceOrder.fulfilled, (state) => {
        // 列表会在刷新后更新，这里不需要特殊处理
      });
  }
});

export const { setPagination } = customServiceOrderSlice.actions;
export default customServiceOrderSlice.reducer; 